import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ArrowRightLeft, CalendarPlus, CheckCircle2, Copy, Link2, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import type {
  Child,
  FamilyCustodyEvent,
  FamilyCustodyEventStatus,
  FamilyCustodyHandoverKind,
  FamilyCustodyRequest,
  FamilyCustodyRequestStatus,
  FamilyCustodyRequestType,
  FamilyCustodyShare,
} from '@/types';
import { familyCustodyActorLabel, mapFamilyCustodyShareRow } from '@/lib/family-custody';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import {
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogDescription as DialogDescription,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
} from '../../../ui-foundation/adapters/web';

const HANDOVER_KIND_OPTIONS: Array<{ value: FamilyCustodyHandoverKind; label: string }> = [
  { value: 'handover', label: 'Handover' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'drop-off', label: 'Drop-off' },
];

const REQUEST_TYPE_OPTIONS: Array<{ value: FamilyCustodyRequestType; label: string }> = [
  { value: 'swap', label: 'Swap day' },
  { value: 'extra-night', label: 'Extra night' },
  { value: 'time-change', label: 'Time change' },
  { value: 'note', label: 'Written note' },
];

const HANDOVER_STATUS_STYLES: Record<FamilyCustodyEventStatus, string> = {
  planned: 'bg-mustard-light text-accent',
  confirmed: 'bg-plum-light text-primary',
  done: 'bg-success-light text-success',
  changed: 'bg-teal-light text-teal',
};

const REQUEST_STATUS_STYLES: Record<FamilyCustodyRequestStatus, string> = {
  proposed: 'bg-mustard-light text-accent',
  accepted: 'bg-success-light text-success',
  declined: 'bg-plum-light text-primary',
  withdrawn: 'bg-muted text-muted-foreground',
};

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function labelForHandoverKind(kind: FamilyCustodyHandoverKind) {
  if (kind === 'drop-off') return 'Drop-off';
  if (kind === 'pickup') return 'Pickup';
  return 'Handover';
}

function labelForRequestType(type: FamilyCustodyRequestType) {
  if (type === 'extra-night') return 'Extra night';
  if (type === 'time-change') return 'Time change';
  if (type === 'note') return 'Written note';
  return 'Swap day';
}

function resolveSelectedChild(selectedChildId: string, manualChildName: string, familyChildren: Child[]) {
  const matchedChild = familyChildren.find((child) => child.id === selectedChildId) || null;
  return {
    childId: matchedChild?.id || null,
    childName: matchedChild?.name.trim() || manualChildName.trim() || 'Family schedule',
  };
}

export default function FamilyCustodySection({
  familyChildren,
  custodyShare,
  custodyEvents,
  custodyRequests,
}: {
  familyChildren: Child[];
  custodyShare: FamilyCustodyShare | null;
  custodyEvents: FamilyCustodyEvent[];
  custodyRequests: FamilyCustodyRequest[];
}) {
  const { data, user } = useApp();
  const namedChildren = useMemo(
    () => familyChildren.filter((child) => child.name.trim().length > 0),
    [familyChildren],
  );
  const displayName = data.profile.firstName.trim() || user?.email?.split('@')[0] || 'Parent';
  const isShareOwner = Boolean(custodyShare && user && custodyShare.ownerUserId === user.id);
  const otherParentName = custodyShare
    ? (isShareOwner ? custodyShare.memberDisplayName : custodyShare.ownerDisplayName)
    : null;

  const [handoverOpen, setHandoverOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [savingHandover, setSavingHandover] = useState(false);
  const [savingRequest, setSavingRequest] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);
  const [joiningShare, setJoiningShare] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  const [handoverChildId, setHandoverChildId] = useState(namedChildren[0]?.id || 'manual');
  const [handoverChildName, setHandoverChildName] = useState(namedChildren[0]?.name || '');
  const [handoverDate, setHandoverDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [handoverTime, setHandoverTime] = useState('15:30');
  const [handoverKind, setHandoverKind] = useState<FamilyCustodyHandoverKind>('handover');
  const [handoverLocation, setHandoverLocation] = useState('');
  const [handoverNotes, setHandoverNotes] = useState('');

  const [requestChildId, setRequestChildId] = useState(namedChildren[0]?.id || 'manual');
  const [requestChildName, setRequestChildName] = useState(namedChildren[0]?.name || '');
  const [requestDate, setRequestDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [requestType, setRequestType] = useState<FamilyCustodyRequestType>('swap');
  const [requestTitle, setRequestTitle] = useState('');
  const [requestDetails, setRequestDetails] = useState('');

  const upcomingHandovers = useMemo(
    () => custodyEvents
      .slice()
      .sort((left, right) => {
        const leftKey = `${left.handoverDate}T${left.handoverTime || '23:59'}`;
        const rightKey = `${right.handoverDate}T${right.handoverTime || '23:59'}`;
        return new Date(leftKey).getTime() - new Date(rightKey).getTime();
      })
      .slice(0, 6),
    [custodyEvents],
  );

  const requestLog = useMemo(
    () => custodyRequests
      .slice()
      .sort((left, right) => {
        const leftKey = left.createdAt || left.requestedForDate;
        const rightKey = right.createdAt || right.requestedForDate;
        return new Date(rightKey).getTime() - new Date(leftKey).getTime();
      })
      .slice(0, 6),
    [custodyRequests],
  );

  const openRequestCount = useMemo(
    () => custodyRequests.filter((request) => request.status === 'proposed').length,
    [custodyRequests],
  );

  const resetHandoverForm = () => {
    setHandoverChildId(namedChildren[0]?.id || 'manual');
    setHandoverChildName(namedChildren[0]?.name || '');
    setHandoverDate(format(new Date(), 'yyyy-MM-dd'));
    setHandoverTime('15:30');
    setHandoverKind('handover');
    setHandoverLocation('');
    setHandoverNotes('');
  };

  const resetRequestForm = () => {
    setRequestChildId(namedChildren[0]?.id || 'manual');
    setRequestChildName(namedChildren[0]?.name || '');
    setRequestDate(format(new Date(), 'yyyy-MM-dd'));
    setRequestType('swap');
    setRequestTitle('');
    setRequestDetails('');
  };

  const moveExistingRowsIntoSharedTrail = async (shareId: string) => {
    if (!user) return;

    const [{ error: eventsError }, { error: requestsError }] = await Promise.all([
      supabase
        .from('family_custody_events' as any)
        .update({ share_id: shareId })
        .eq('user_id', user.id)
        .is('share_id', null),
      supabase
        .from('family_custody_requests' as any)
        .update({ share_id: shareId })
        .eq('user_id', user.id)
        .is('share_id', null),
    ]);

    if (eventsError || requestsError) {
      throw eventsError || requestsError;
    }
  };

  const createSharedTrail = async () => {
    if (!user) return;

    setCreatingShare(true);
    const { data: shareRow, error } = await supabase.rpc('create_family_custody_share' as any, {
      owner_display_name: displayName,
    });
    setCreatingShare(false);

    if (error) {
      toast.error(error.message || 'Could not create the shared-custody code right now.');
      return;
    }

    const nextShare = mapFamilyCustodyShareRow(shareRow);
    if (!nextShare) {
      toast.error('Could not create the shared-custody code right now.');
      return;
    }

    try {
      await moveExistingRowsIntoSharedTrail(nextShare.id);
    } catch (moveError) {
      console.error(moveError);
      toast.error('Shared view created, but older rows could not be moved across yet.');
      notifySectionRefresh();
      return;
    }

    toast.success('Shared custody code ready.');
    notifySectionRefresh();
  };

  const joinSharedTrail = async () => {
    if (!user) return;
    if (!joinCode.trim()) {
      toast.error('Add the shared-custody code first.');
      return;
    }

    setJoiningShare(true);
    const { data: shareRow, error } = await supabase.rpc('join_family_custody_share' as any, {
      share_invite_code: joinCode.trim().toUpperCase(),
      joining_display_name: displayName,
    });
    setJoiningShare(false);

    if (error) {
      toast.error(error.message || 'Could not join that shared-custody code.');
      return;
    }

    const nextShare = mapFamilyCustodyShareRow(shareRow);
    if (!nextShare) {
      toast.error('Could not join that shared-custody code.');
      return;
    }

    try {
      await moveExistingRowsIntoSharedTrail(nextShare.id);
    } catch (moveError) {
      console.error(moveError);
      toast.error('Joined the shared view, but older rows could not be moved across yet.');
      notifySectionRefresh();
      return;
    }

    setJoinCode('');
    toast.success('Shared custody is now visible to both parents.');
    notifySectionRefresh();
  };

  const copyInviteCode = async () => {
    if (!custodyShare?.inviteCode) return;

    try {
      await navigator.clipboard.writeText(custodyShare.inviteCode);
      toast.success('Shared-custody code copied.');
    } catch (error) {
      console.error(error);
      toast.error(`Copy failed. The code is ${custodyShare.inviteCode}.`);
    }
  };

  const saveHandover = async () => {
    if (!user) return;
    if (!handoverDate) {
      toast.error('Add the handover date first.');
      return;
    }

    const resolvedChild = resolveSelectedChild(handoverChildId, handoverChildName, namedChildren);
    setSavingHandover(true);

    const { error } = await supabase.from('family_custody_events' as any).insert({
      user_id: user.id,
      share_id: custodyShare?.id || null,
      child_id: resolvedChild.childId,
      child_name: resolvedChild.childName,
      handover_date: handoverDate,
      handover_time: handoverTime || null,
      handover_kind: handoverKind,
      location: handoverLocation.trim() || null,
      notes: handoverNotes.trim() || null,
    });

    setSavingHandover(false);

    if (error) {
      toast.error('Could not save that handover right now.');
      return;
    }

    toast.success('Handover saved.');
    notifySectionRefresh();
    resetHandoverForm();
    setHandoverOpen(false);
  };

  const saveRequest = async () => {
    if (!user) return;
    if (!requestDate) {
      toast.error('Add the request date first.');
      return;
    }
    if (!requestTitle.trim()) {
      toast.error('Give the request a short title first.');
      return;
    }

    const resolvedChild = resolveSelectedChild(requestChildId, requestChildName, namedChildren);
    setSavingRequest(true);

    const { error } = await supabase.from('family_custody_requests' as any).insert({
      user_id: user.id,
      share_id: custodyShare?.id || null,
      child_id: resolvedChild.childId,
      child_name: resolvedChild.childName,
      requested_for_date: requestDate,
      request_type: requestType,
      title: requestTitle.trim(),
      details: requestDetails.trim() || null,
    });

    setSavingRequest(false);

    if (error) {
      toast.error('Could not save that co-parenting request right now.');
      return;
    }

    toast.success('Co-parenting request saved.');
    notifySectionRefresh();
    resetRequestForm();
    setRequestOpen(false);
  };

  const updateHandoverStatus = async (id: string, status: FamilyCustodyEventStatus) => {
    const { error } = await supabase.from('family_custody_events' as any).update({ status }).eq('id', id);
    if (error) {
      toast.error('Could not update that handover right now.');
      return;
    }
    toast.success('Handover updated.');
    notifySectionRefresh();
  };

  const updateRequestStatus = async (id: string, status: FamilyCustodyRequestStatus) => {
    const { error } = await supabase.from('family_custody_requests' as any).update({ status }).eq('id', id);
    if (error) {
      toast.error('Could not update that request right now.');
      return;
    }
    toast.success('Request updated.');
    notifySectionRefresh();
  };

  return (
    <>
      <AppCard>
        <AppSectionHeader
          title="Shared custody"
          subtitle="Keep handovers and schedule-change requests in one written trail on this account so they do not disappear into messages."
          action={(
            <AppButtonRow>
              <AppButton tone="outline" size="sm" onPress={() => setHandoverOpen(true)}>
                <CalendarPlus size={14} />
                Add handover
              </AppButton>
              <AppButton size="sm" onPress={() => setRequestOpen(true)}>
                <MessageSquarePlus size={14} />
                Request change
              </AppButton>
            </AppButtonRow>
          )}
        />

        {custodyShare ? (
          <AppCard backgroundColor="$background">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <AppText fontSize="$sm" fontWeight="700">
                  {custodyShare.status === 'active'
                    ? `Shared with both parents${otherParentName ? ` · ${otherParentName}` : ''}`
                    : 'Shared view is ready'}
                </AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                  {custodyShare.status === 'active'
                    ? 'New handovers, notes, and request changes now stay visible to both parents in the same Family trail.'
                    : 'Share this code with the other parent. New handovers and requests saved here will appear to both once they join.'}
                </AppText>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full border border-border bg-card px-3 py-1.5 text-[11px] font-semibold tracking-[0.16em] text-foreground">
                  {custodyShare.inviteCode}
                </div>
                <AppButton tone="outline" size="sm" onPress={() => void copyInviteCode()}>
                  <Copy size={12} /> Copy code
                </AppButton>
              </div>
            </div>
          </AppCard>
        ) : (
          <AppCard backgroundColor="$background" borderStyle="dashed">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <AppText fontSize="$sm" fontWeight="700">Make the custody trail visible to both parents</AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                  Create a shared code or join an existing one. Adulting will move this account&apos;s current handovers and requests into the shared trail so both parents can see the same record.
                </AppText>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <AppButton tone="outline" size="sm" onPress={() => void createSharedTrail()} disabled={creatingShare}>
                  <Link2 size={12} />
                  {creatingShare ? 'Creating…' : 'Create shared code'}
                </AppButton>
                <div className="flex gap-2">
                  <AppInput
                    value={joinCode}
                    onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="h-9 w-[120px]"
                  />
                  <AppButton size="sm" onPress={() => void joinSharedTrail()} disabled={joiningShare}>
                    {joiningShare ? 'Joining…' : 'Join code'}
                  </AppButton>
                </div>
              </div>
            </div>
          </AppCard>
        )}

        {openRequestCount > 0 ? (
          <AppCard backgroundColor="$background">
            <AppText fontSize="$sm" fontWeight="700">
              {openRequestCount} co-parenting request{openRequestCount === 1 ? '' : 's'} still need a decision or follow-up.
            </AppText>
            <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
              Keep accepted, declined, and withdrawn changes here so the trail stays readable later.
            </AppText>
          </AppCard>
        ) : null}

        <div className="grid gap-4 xl:grid-cols-2">
          <AppCard backgroundColor="$background">
            <div className="flex items-center gap-2">
              <ArrowRightLeft size={16} className="text-primary" />
              <AppText fontSize="$sm" fontWeight="700">Upcoming handovers</AppText>
            </div>
            {upcomingHandovers.length > 0 ? (
              <div className="mt-3 space-y-3">
                {upcomingHandovers.map((item) => (
                  <AppCard key={item.id}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <AppText fontSize="$sm" fontWeight="700">
                          {item.childName || 'Family schedule'} {labelForHandoverKind(item.handoverKind)}
                        </AppText>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {format(parseISO(item.handoverDate), 'EEE d MMM')}
                          {item.handoverTime ? ` · ${item.handoverTime}` : ''}
                          {item.location ? ` · ${item.location}` : ''}
                        </AppText>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {familyCustodyActorLabel(custodyShare, user?.id || null, item.userId)}
                        </AppText>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${HANDOVER_STATUS_STYLES[item.status]}`}>
                        {item.status}
                      </span>
                    </div>
                    {item.notes ? <AppText className="mt-2 leading-relaxed" fontSize="$xs" color="$colorMuted">{item.notes}</AppText> : null}
                    {item.status !== 'done' ? (
                      <AppButtonRow mt="$sm">
                        {item.status === 'planned' ? (
                          <AppButton size="sm" tone="outline" onPress={() => void updateHandoverStatus(item.id, 'confirmed')}>
                            Confirm
                          </AppButton>
                        ) : null}
                        {item.status !== 'changed' ? (
                          <AppButton size="sm" tone="outline" onPress={() => void updateHandoverStatus(item.id, 'changed')}>
                            Mark changed
                          </AppButton>
                        ) : null}
                        <AppButton size="sm" tone="outline" onPress={() => void updateHandoverStatus(item.id, 'done')}>
                          <CheckCircle2 size={12} /> Done
                        </AppButton>
                      </AppButtonRow>
                    ) : null}
                  </AppCard>
                ))}
              </div>
            ) : (
              <AppEmptyState
                title="No handovers saved yet"
                body="Add the next pickup, drop-off, or handover date so it is visible in the calendar shell."
              />
            )}
          </AppCard>

          <AppCard backgroundColor="$background">
            <div className="flex items-center gap-2">
              <MessageSquarePlus size={16} className="text-primary" />
              <AppText fontSize="$sm" fontWeight="700">Request log</AppText>
            </div>
            {requestLog.length > 0 ? (
              <div className="mt-3 space-y-3">
                {requestLog.map((item) => (
                  <AppCard key={item.id}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <AppText fontSize="$sm" fontWeight="700">{item.title}</AppText>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {item.childName || 'Family schedule'} · {labelForRequestType(item.requestType)} · {format(parseISO(item.requestedForDate), 'EEE d MMM')}
                        </AppText>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {familyCustodyActorLabel(custodyShare, user?.id || null, item.userId)}
                        </AppText>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${REQUEST_STATUS_STYLES[item.status]}`}>
                        {item.status}
                      </span>
                    </div>
                    {item.details ? <AppText className="mt-2 leading-relaxed" fontSize="$xs" color="$colorMuted">{item.details}</AppText> : null}
                    {item.status === 'proposed' ? (
                      <AppButtonRow mt="$sm">
                        <AppButton size="sm" tone="outline" onPress={() => void updateRequestStatus(item.id, 'accepted')}>
                          Accept
                        </AppButton>
                        <AppButton size="sm" tone="outline" onPress={() => void updateRequestStatus(item.id, 'declined')}>
                          Decline
                        </AppButton>
                        <AppButton size="sm" tone="outline" onPress={() => void updateRequestStatus(item.id, 'withdrawn')}>
                          Withdraw
                        </AppButton>
                      </AppButtonRow>
                    ) : null}
                  </AppCard>
                ))}
              </div>
            ) : (
              <AppEmptyState
                title="No request trail yet"
                body="Save the next swap, time change, or written note here instead of leaving it only in messages."
              />
            )}
          </AppCard>
        </div>
      </AppCard>

      <Dialog
        open={handoverOpen}
        onOpenChange={(open) => {
          if (!open) resetHandoverForm();
          setHandoverOpen(open);
        }}
      >
        <DialogContent className="max-w-lg">
          <AppCardBody gap="$md">
            <DialogHeader>
              <DialogTitle>Add handover</DialogTitle>
              <DialogDescription>
                Record the next pickup, drop-off, or handover day with the detail you would want quickly to hand.
              </DialogDescription>
            </DialogHeader>
            {namedChildren.length > 0 ? (
              <AppFieldGroup label="Child">
                <Select
                  value={handoverChildId}
                  onValueChange={(value) => {
                    setHandoverChildId(value);
                    const selectedChild = namedChildren.find((child) => child.id === value);
                    if (selectedChild) {
                      setHandoverChildName(selectedChild.name);
                    } else {
                      setHandoverChildName('');
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Choose child" /></SelectTrigger>
                  <SelectContent>
                    {namedChildren.map((child) => (
                      <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                    ))}
                    <SelectItem value="manual">Other / multiple children</SelectItem>
                  </SelectContent>
                </Select>
              </AppFieldGroup>
            ) : null}

            {(namedChildren.length === 0 || handoverChildId === 'manual') ? (
              <AppFieldGroup label="Child or schedule label">
                <AppInput
                  placeholder="Child or schedule label"
                  value={handoverChildName}
                  onChange={(event) => setHandoverChildName(event.target.value)}
                />
              </AppFieldGroup>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <AppDateField label="Date" value={handoverDate} onChange={setHandoverDate} />
              <AppFieldGroup label="Time">
                <AppInput type="time" value={handoverTime} onChange={(event) => setHandoverTime(event.target.value)} />
              </AppFieldGroup>
            </div>

            <AppFieldGroup label="Handover type">
              <Select value={handoverKind} onValueChange={(value) => setHandoverKind(value as FamilyCustodyHandoverKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HANDOVER_KIND_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </AppFieldGroup>

            <AppFieldGroup label="Location">
              <AppInput
                placeholder="Location or handover point"
                value={handoverLocation}
                onChange={(event) => setHandoverLocation(event.target.value)}
              />
            </AppFieldGroup>

            <AppFieldGroup label="Notes">
              <AppTextArea
                placeholder="Anything that matters this week: medication, uniform, pickups, or what changed."
                value={handoverNotes}
                onChange={(event) => setHandoverNotes(event.target.value)}
                rows={4}
              />
            </AppFieldGroup>

            <AppButtonRow justifyContent="flex-end">
              <AppButton onPress={() => void saveHandover()} disabled={savingHandover}>
                {savingHandover ? 'Saving…' : 'Save handover'}
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </DialogContent>
      </Dialog>

      <Dialog
        open={requestOpen}
        onOpenChange={(open) => {
          if (!open) resetRequestForm();
          setRequestOpen(open);
        }}
      >
        <DialogContent className="max-w-lg">
          <AppCardBody gap="$md">
            <DialogHeader>
              <DialogTitle>Request change</DialogTitle>
              <DialogDescription>
                Keep the next swap, time change, or written co-parenting note inside Family so the decision trail is easier to follow later.
              </DialogDescription>
            </DialogHeader>
            {namedChildren.length > 0 ? (
              <AppFieldGroup label="Child">
                <Select
                  value={requestChildId}
                  onValueChange={(value) => {
                    setRequestChildId(value);
                    const selectedChild = namedChildren.find((child) => child.id === value);
                    if (selectedChild) {
                      setRequestChildName(selectedChild.name);
                    } else {
                      setRequestChildName('');
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Choose child" /></SelectTrigger>
                  <SelectContent>
                    {namedChildren.map((child) => (
                      <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                    ))}
                    <SelectItem value="manual">Other / multiple children</SelectItem>
                  </SelectContent>
                </Select>
              </AppFieldGroup>
            ) : null}

            {(namedChildren.length === 0 || requestChildId === 'manual') ? (
              <AppFieldGroup label="Child or schedule label">
                <AppInput
                  placeholder="Child or schedule label"
                  value={requestChildName}
                  onChange={(event) => setRequestChildName(event.target.value)}
                />
              </AppFieldGroup>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-[1fr_1.2fr]">
              <AppDateField label="Date" value={requestDate} onChange={setRequestDate} />
              <AppFieldGroup label="Request type">
                <Select
                  value={requestType}
                  onValueChange={(value) => {
                    setRequestType(value as FamilyCustodyRequestType);
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REQUEST_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </AppFieldGroup>
            </div>

            <AppFieldGroup label="Short title">
              <AppInput
                placeholder="Short title, for example Swap Saturday pickup"
                value={requestTitle}
                onChange={(event) => setRequestTitle(event.target.value)}
              />
            </AppFieldGroup>

            <AppFieldGroup label="Details">
              <AppTextArea
                placeholder="Write the actual change or note you need to keep in writing."
                value={requestDetails}
                onChange={(event) => setRequestDetails(event.target.value)}
                rows={4}
              />
            </AppFieldGroup>

            <AppButtonRow justifyContent="flex-end">
              <AppButton onPress={() => void saveRequest()} disabled={savingRequest}>
                {savingRequest ? 'Saving…' : 'Save request'}
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
